/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useMemo, useRef } from 'react'
import { useFlexLayout, usePagination, useResizeColumns, useSortBy, useTable } from 'react-table'
import classnames from 'classnames'
import { useSelector } from 'react-redux'
import { Tooltip } from '../Tooltip/Tooltip'
import { OtherAnalysesModal } from './components/OtherAnalysesModal'

const ProsangTable = ({ data, columns, resizable, sortable, paginated, onRowClick }) => {
  const tableRef = useRef(null)
  const { donationHBUnit, donationBPUnit, donationFerritinUnit } = useSelector(
    (state) => state.app.appSettings,
  )

  const dateSort = useCallback(
    (rowA, rowB) => (rowA.original.originalDate > rowB.original.originalDate ? 1 : -1),
    [],
  )

  const sortTypes = {
    dateSort,
  }

  const compactData = useMemo(() => _.compact(data), [data])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state,
  } = useTable(
    {
      columns,
      data: compactData,
      initialState: {
        pageIndex: 0,
        pageSize: paginated ? (typeof paginated === 'number' ? paginated : 10) : 999999,
        hiddenColumns: _(columns)
          .filter({ hidden: true })
          .map((c) => c.accessor)
          .value(),
      },
      sortTypes,
    },
    useFlexLayout,
    useSortBy,
    usePagination,
    useResizeColumns,
  )

  if (_.filter(data, (x) => x).length === 0) return null
  const { pageIndex } = state

  const handleRowClick = (row) => {
    if (row && typeof onRowClick === 'function') {
      onRowClick(row)
    }
  }

  const getBpUnit = () => donationBPUnit || 'mm Hg'
  const getHbUnit = () => donationHBUnit || 'g/l'
  const getFerritinUnit = () => donationFerritinUnit || ''
  return (
    <div className={classnames('prosang-table w-100', { resizable })} ref={tableRef}>
      <div {...getTableProps()} className="table">
        <div className="thead">
          {_.map(headerGroups, (headerGroup) => (
            <div {...headerGroup.getHeaderGroupProps()} className="tr">
              {_.map(headerGroup.headers, (column) => (
                <div
                  {...column.getHeaderProps(sortable && column.getSortByToggleProps())}
                  className="th"
                >
                  {column.render('Header')}
                  {/* Use column.getResizerProps to hook up the events correctly */}
                  {column.canResize && (
                    <div
                      {...column.getResizerProps()}
                      className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                    />
                  )}
                  <span>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <i className="icon icon-geblod_ikon_cheveron-down" />
                      ) : (
                        <i className="icon icon-geblod_ikon_cheveron-up" />
                      )
                    ) : (
                      ''
                    )}
                  </span>
                  {column.description && (
                    <Tooltip side="left" content={<div>{column.description}</div>}>
                      <div id={column.id} className="header-tooltip">
                        <i className="icon icon-geblod_ikon_info" />
                      </div>
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="tbody" {...getTableBodyProps()}>
          {_.map(page, (row) => {
            prepareRow(row)
            return (
              <div
                {...row.getRowProps()}
                className={classnames('tr', { 'cursor-pointer': !!onRowClick })}
                onClick={() => handleRowClick(row)}
              >
                {_.map(row.cells, (cell) => (
                  <div {...cell.getCellProps()} className="td">
                    {cell.column.id === 'donationOtherAnalyses' ? (
                      <OtherAnalysesModal analyses={cell.value} date={row.original.donationDate} />
                    ) : (
                      <>
                        {cell.render('Cell')} {''}
                        {cell.value && cell.column.id === 'donationHB' && getHbUnit()}
                        {cell.value && cell.column.id === 'donationBP' && getBpUnit()}
                        {cell.value && cell.column.id === 'donationFerritin' && getFerritinUnit()}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
      {paginated && pageCount > 1 && (
        <div className="pagination d-flex justify-content-between align-items-center">
          <div className="btn-group btn-group-sm">
            <button
              className="btn theme-button-primary"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              {'<<'}
            </button>
            <button
              className="btn theme-button-primary"
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              {'<'}
            </button>
          </div>

          <div className="page-info">
            <span>
              {__.trans('app.table_page')}{' '}
              <strong>
                {pageIndex + 1} {__.trans('app.table_of')} {pageOptions.length}
              </strong>{' '}
            </span>
          </div>

          <div className="btn-group btn-group-sm">
            <button
              className="btn theme-button-primary"
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              {'>'}
            </button>
            <button
              className="btn theme-button-primary"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              {'>>'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProsangTable
